<template>
  <div class="col-sm-12">

    <div class="card mb-3">
      <div class="card-header">
        <h4 class="mb-0">Activos</h4>
      </div>
      <div class="card-body">
        <div class="row g-2 align-items-end">
          <div class="col-md-4">
            <label class="form-label mb-1">Archivo XLSX</label>
            <input
              ref="fileInput"
              class="form-control"
              type="file"
              accept=".xlsx,.xls"
              :disabled="cargando"
              @change="onFileChange"
            />
          </div>
          <div class="col-auto" v-if="rows.length">
            <button type="button" class="btn btn-outline-secondary" @click="limpiar">
              <vue-feather type="x" size="14" class="me-1" /> Quitar archivo
            </button>
          </div>
          <div class="col-auto">
            <small v-if="cargando" class="text-muted">Procesando archivo...</small>
          </div>
        </div>
        <div v-if="error" class="alert alert-danger mt-3 mb-0 py-2">{{ error }}</div>
      </div>
    </div>

    <div v-if="rows.length" class="card">
      <div class="card-header">
        <div class="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <h6 class="mb-0 fw-bold">{{ archivoNombre }}</h6>
          <div class="text-end">
            <small class="text-muted d-block">{{ rows.length }} registro(s)</small>
            <button type="button" class="btn btn-sm btn-primary mt-1" @click="cargar">
              <vue-feather type="upload" size="14" class="me-1" /> Cargar
            </button>
          </div>
        </div>
        <div class="row g-3 mt-1">
          <div class="col-auto">
            <small class="text-muted d-block">Importe total</small>
            <span class="fw-bold">{{ formatCurrency(importeTotal) }}</span>
          </div>
        </div>
      </div>
      <div class="card-body p-0">
        <DataTable :rows="rows" :columns="columns" :show-actions="false" :row-class="rowClass">
          <template #cell-IMPORTE="{ value }">
            {{ formatCurrency(value) }}
          </template>
          <template #header-_cargar>
            Cargar
            <input type="checkbox" :checked="todosSeleccionados" @change="toggleTodos" />
          </template>
          <template #cell-_cargar="{ item }">
            <input
              type="checkbox"
              v-model="item._cargar"
              :disabled="item._verificando || item._verificado === false"
              @change="onToggleCargar(item)"
            />
          </template>
        </DataTable>
      </div>
    </div>

  </div>
</template>

<script>
import * as XLSX from 'xlsx';
import DataTable from '@/components/DataTable.vue';

const REQUIRED_COLUMNS = ['DEPENDENCIA', 'QNA', 'POLIZA', 'RFC', 'EMPLEADO', 'IMPORTE'];

// Ejemplo: RFCs que no existen en el catálogo del contratante (simulado).
const RFCS_INVALIDOS_EJEMPLO = ['TOGC710201HA0', 'ROSJ701015SH8'];

function normalize(value) {
  return value == null ? '' : String(value).trim().toUpperCase();
}

function delay(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

// Ejemplo de verificación: simula una consulta asíncrona.
// TODO: sustituir por la llamada real, p.ej. api.get(`polizas/existe/${poliza}`).
async function verificarPoliza(poliza) {
  await delay(300);
  return !!poliza && String(poliza).trim().length >= 5;
}

// Ejemplo de verificación: simula una consulta asíncrona.
// TODO: sustituir por la llamada real, p.ej. api.get(`contratantes/existe/${rfc}`).
async function verificarRfc(rfc) {
  await delay(300);
  return !RFCS_INVALIDOS_EJEMPLO.includes(normalize(rfc));
}

export default {
  name: 'Activos',
  components: { DataTable },

  data() {
    return {
      cargando: false,
      error: null,
      archivoNombre: '',
      rows: [],
      columns: [
        { text: 'DEPENDENCIA', value: 'DEPENDENCIA' },
        { text: 'QNA', value: 'QNA' },
        { text: 'POLIZA', value: 'POLIZA' },
        { text: 'RFC', value: 'RFC' },
        { text: 'EMPLEADO', value: 'EMPLEADO' },
        { text: 'IMPORTE', value: 'IMPORTE' },
        { text: 'Cargar', value: '_cargar' },
      ],
    };
  },

  computed: {
    importeTotal() {
      return this.rows.reduce((sum, row) => sum + (Number(row.IMPORTE) || 0), 0);
    },

    todosSeleccionados() {
      return this.rows.length > 0 && this.rows.every(row => row._cargar);
    },
  },

  methods: {
    async onFileChange(e) {
      const file = e.target.files && e.target.files[0];
      if (!file) return;

      this.error = null;
      this.rows = [];
      this.archivoNombre = file.name;
      this.cargando = true;

      try {
        const buffer = await file.arrayBuffer();
        const workbook = XLSX.read(buffer, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const matrix = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: null });

        const headerRowIndex = matrix.findIndex(row =>
          REQUIRED_COLUMNS.every(col => row.some(cell => normalize(cell) === col))
        );

        if (headerRowIndex === -1) {
          this.error = `No se encontraron las columnas esperadas (${REQUIRED_COLUMNS.join(', ')}) en el archivo.`;
          return;
        }

        const headerRow = matrix[headerRowIndex];
        const colIndex = {};
        REQUIRED_COLUMNS.forEach(col => {
          colIndex[col] = headerRow.findIndex(cell => normalize(cell) === col);
        });

        this.rows = matrix
          .slice(headerRowIndex + 1)
          .filter(row => row.some(cell => cell !== null && cell !== ''))
          .map(row => ({
            DEPENDENCIA: row[colIndex.DEPENDENCIA] ?? null,
            QNA: row[colIndex.QNA] ?? null,
            POLIZA: row[colIndex.POLIZA] ?? null,
            RFC: row[colIndex.RFC] ?? null,
            EMPLEADO: row[colIndex.EMPLEADO] ?? null,
            IMPORTE: row[colIndex.IMPORTE] ?? null,
            _cargar: false,
            _verificando: false,
            _verificado: null,
          }));

        if (!this.rows.length) {
          this.error = 'El archivo no contiene registros.';
        }
      } catch (err) {
        console.error('Error al procesar el archivo:', err);
        this.error = 'No se pudo leer el archivo. Verifique que sea un XLSX válido.';
      } finally {
        this.cargando = false;
      }
    },

    limpiar() {
      this.rows = [];
      this.error = null;
      this.archivoNombre = '';
      if (this.$refs.fileInput) this.$refs.fileInput.value = '';
    },

    toggleTodos(e) {
      const val = e.target.checked;
      this.rows.forEach(row => {
        if (val && row._verificado === false) return;
        row._cargar = val;
        this.onToggleCargar(row);
      });
    },

    async onToggleCargar(row) {
      if (!row._cargar) {
        row._verificado = null;
        return;
      }
      row._verificando = true;
      const [polizaOk, rfcOk] = await Promise.all([
        verificarPoliza(row.POLIZA),
        verificarRfc(row.RFC),
      ]);
      row._verificando = false;
      row._verificado = polizaOk && rfcOk;
      if (!row._verificado) {
        row._cargar = false;
      }
    },

    rowClass(item) {
      if (item._verificado === true) return 'text-success';
      if (item._verificado === false) return 'text-danger';
      return '';
    },

    // TODO: definir la lógica/endpoint de carga en una siguiente solicitud.
    cargar() {
      console.log('Cargar registros', this.rows);
    },

    formatCurrency(value) {
      if (value == null || value === '') return '—';
      const num = Number(value);
      if (Number.isNaN(num)) return String(value);
      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(num);
    },
  },
};
</script>
